import React, { useMemo, useState, useRef, Component } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { HeadphonesIcon, ChevronRightIcon, StarIcon, TrendingUpIcon, UsersIcon, TagIcon, LibraryIcon } from 'lucide-react';
import { useDarkMode } from '../context/DarkModeContext';
import { useDarkClasses } from '../hooks/useDarkClasses';
import { useRoleTheme } from '../hooks/useRoleTheme';
import { SearchWithAutocomplete, Suggestion } from '../components/UI/SearchWithAutocomplete';
import AudiobookCard from '../components/Books/AudiobookCard';
import { audiobooks as realAudiobooks } from '../AudioBookDetails';
import { FilterSidebar, FilterSidebarWrapper, NavItem } from '../components/UI/FilterSidebar';
// Build unified search suggestions
const buildSearchSuggestions = (): Suggestion[] => {
  const suggestions: Suggestion[] = [];
  const seenAuthors = new Set<string>();
  const seenNarrators = new Set<string>();
  realAudiobooks.forEach(book => {
    suggestions.push({
      id: `book-${book.title}`,
      label: book.title,
      sublabel: `by ${book.author}`,
      type: 'Audiobook',
      image: book.coverImage
    });
    if (!seenAuthors.has(book.author)) {
      seenAuthors.add(book.author);
      suggestions.push({
        id: `author-${book.author}`,
        label: book.author,
        sublabel: 'Author',
        type: 'Author'
      });
    }
    book.narrators.forEach(narrator => {
      if (!seenNarrators.has(narrator)) {
        seenNarrators.add(narrator);
        suggestions.push({
          id: `narrator-${narrator}`,
          label: narrator,
          sublabel: 'Narrator',
          type: 'Narrator'
        });
      }
    });
  });
  return suggestions;
};
const AudiobookBestsellers = () => {
  const {
    darkMode
  } = useDarkMode();
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [currentlyPlaying, setCurrentlyPlaying] = useState<string | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const audioRefs = useRef<{
    [key: string]: HTMLAudioElement;
  }>({});
  const searchSuggestions = useMemo(() => buildSearchSuggestions(), []);
  // Simulate "Bestsellers" by sorting by rating (mock logic)
  const bestsellers = [...realAudiobooks].sort((a, b) => (b.spiceLevel || 0) - (a.spiceLevel || 0));
  const handleSearchSelect = (suggestion: Suggestion) => {
    if (suggestion.type === 'Audiobook') {
      const slug = suggestion.label.replace(/\s+/g, '-').toLowerCase();
      navigate(`/book/${slug}`);
    } else if (suggestion.type === 'Author') {
      const slug = suggestion.label.toLowerCase().replace(/\s+/g, '-');
      navigate(`/author/${slug}`);
    } else if (suggestion.type === 'Narrator') {
      const slug = suggestion.label.toLowerCase().replace(/\s+/g, '-');
      navigate(`/narrators/${slug}`);
    } else {
      navigate(`/search?q=${encodeURIComponent(suggestion.label)}`);
    }
    setSearchQuery('');
  };
  const handleSearch = (query: string) => {
    navigate(`/search?q=${encodeURIComponent(query)}`);
  };
  const handlePlayPause = (bookId: string) => {
    if (currentlyPlaying === bookId) {
      if (audioRefs.current[bookId]) {
        audioRefs.current[bookId].pause();
      }
      setCurrentlyPlaying(null);
    } else {
      if (currentlyPlaying && audioRefs.current[currentlyPlaying]) {
        audioRefs.current[currentlyPlaying].pause();
      }
      if (audioRefs.current[bookId]) {
        audioRefs.current[bookId].play();
        setCurrentlyPlaying(bookId);
      }
    }
  };
  const handleAudioEnded = () => {
    setCurrentlyPlaying(null);
  };
  const audiobookNavItems: NavItem[] = [{
    label: 'Audiobooks',
    href: '/audiobooks',
    icon: HeadphonesIcon
  }, {
    label: 'Narrators',
    href: '/narrators',
    icon: UsersIcon
  }, {
    label: 'Genres',
    href: '/audiobooks/genres',
    icon: TagIcon
  }, {
    label: 'New Releases',
    href: '/audiobooks/new',
    icon: StarIcon
  }, {
    label: 'Bestsellers',
    href: '/audiobooks/bestsellers',
    icon: TrendingUpIcon,
    isActive: true
  }, {
    label: 'Series',
    href: '/series',
    icon: LibraryIcon
  }];
  return <div className={`min-h-screen ${dk.page}`} data-id="element-6319" data-mp="1fwz2bv36yrr0">
      {/* Header with Search */}
      <div className={`py-10 ${dk.surface} border-b ${dk.border}`} data-id="element-6320" data-mp="1fz23nb9so7b1">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-6321" data-mp="1kwv6pvfswl73">
          <div className={`flex items-center text-sm mb-4 ${dk.textMuted}`} data-id="element-6322" data-mp="1ox5l9whnsjxk">
            <Link to="/" className={`hover:${dk.textPrimary} transition-colors`} data-id="element-6323" data-mp="1twql094nkwbc">
              Home
            </Link>
            <ChevronRightIcon className="h-4 w-4 mx-2" data-id="element-6324" data-mp="30capx61cosjt" />
            <Link to="/audiobooks" className={`hover:${dk.textPrimary} transition-colors`} data-id="element-6325" data-mp="35bdwiuwzb6tw">
              Audiobooks
            </Link>
            <ChevronRightIcon className="h-4 w-4 mx-2" data-id="element-6326" data-mp="3agr660iqsbo5" />
            <span className={dk.textPrimary} data-id="element-6327" data-mp="3ai2bw5vqzthw">Bestsellers</span>
          </div>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6" data-id="element-6328" data-mp="35p97jj46yn6c">
            <div className="flex items-center gap-3" data-id="element-6329" data-mp="31p8rxv5cw5xp">
              <TrendingUpIcon className={`h-8 w-8 ${rt.textPrimary}`} data-id="element-6330" data-mp="31mcppumh6v3g" />
              <div data-id="element-6331" data-mp="2wqxgxh3brpdw">
                <h1 className={`text-3xl font-bold ${dk.textHeading}`} data-id="element-6332" data-mp="2wn9l2baeqqw0">
                  Bestsellers
                </h1>
                <p className={`mt-1 ${dk.textSecondary}`} data-id="element-6333" data-mp="2mr6nliqi3gfe">
                  Our most popular audiobooks loved by listeners
                </p>
              </div>
            </div>
            <div className="w-full md:max-w-md" data-id="element-6334" data-mp="2irny09nqih68">
              <SearchWithAutocomplete value={searchQuery} onChange={setSearchQuery} suggestions={searchSuggestions} placeholder="Search bestsellers..." variant="rounded" onSelect={handleSearchSelect} onSearch={handleSearch} data-id="element-6335" data-mp="l0374ctxj7mx" />
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-6336" data-mp="21hag9zk25vvb">
        <div className="flex gap-8" data-id="element-6337" data-mp="25hkrbqmpjch1">
          <FilterSidebarWrapper isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} isCollapsed={sidebarCollapsed} onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)} data-id="element-6338" data-mp="25ingnu4dt6uu">
            <FilterSidebar title="Browse" navigation={audiobookNavItems} sections={[]} onReset={() => {}} searchable={false} data-id="element-6339" data-mp="2k6qq5dhmv0oe" />
          </FilterSidebarWrapper>

          <main className="flex-1 min-w-0" data-id="element-6340" data-mp="18qk1vb2v0x8t">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-6" data-id="element-6341" data-mp="18txzy74hqkag">
              {bestsellers.map((book, index) => <div key={`${book.title}-${index}`} className="flex justify-center" data-id="element-6342" data-mp="158wa1ghu210a" data-mpi={index}>
                  <div className="w-full max-w-[260px]" data-id="element-6343" data-mp="117sukv3p66zl">
                    <AudiobookCard title={book.title} author={book.author} coverImage={book.coverImage} narrators={book.narrators} audioLength={book.audioLength} genre={book.genre} relationshipTypes={book.relationshipTypes} audioSampleUrl={book.audioSampleUrl || ''} isPlaying={currentlyPlaying === book.title} onPlayPause={handlePlayPause} spiceLevel={book.spiceLevel} tropes={book.tropes} seriesName={book.series?.name || undefined} seriesNumber={book.series?.number || undefined} seriesStatus={book.series?.seriesComplete ? 'completed' : book.series?.name ? 'ongoing' : undefined} data-id="element-6344" data-mp="1f83m80ctzplt" />
                    <audio ref={el => {
                  if (el) audioRefs.current[book.title] = el;
                }} src={book.audioSampleUrl} onEnded={handleAudioEnded} className="hidden" data-id="element-6345" data-mp="3odgqtrg9ab7z" />
                  </div>
                </div>)}
            </div>
          </main>
        </div>
      </div>
    </div>;
};
export default AudiobookBestsellers;